import { type FocusEvent, useCallback, useEffect, useRef } from 'react';

import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';

type FormSection =
  | 'contacts'
  | 'freight_rates'
  | 'location'
  | 'material_rates'
  | 'materials'
  | 'order_defaults'
  | 'trucks';

export type SectionMs = Partial<Record<FormSection, number>>;

export default function useFormSectionTiming(
  form: 'order_form_v3' | 'project_form_v2',
  isEditing: boolean,
) {
  const sectionMsRef = useRef<SectionMs>({});
  const focusedSectionsRef = useRef(new Set<FormSection>());
  const activeSectionRef = useRef<{ section: FormSection; startedAt: number } | null>(
    null,
  );
  const lastHandledFocusRef = useRef<Event | null>(null);
  const isPageHiddenRef = useRef(document.hidden);

  const accrueActiveSection = useCallback(() => {
    const activeSection = activeSectionRef.current;
    if (!activeSection) return;

    const now = Date.now();
    if (!isPageHiddenRef.current) {
      const { section, startedAt } = activeSection;
      sectionMsRef.current[section] =
        (sectionMsRef.current[section] ?? 0) + now - startedAt;
    }
    activeSection.startedAt = now;
  }, []);

  useEffect(() => {
    const settleOnVisibilityChange = () => {
      accrueActiveSection();
      isPageHiddenRef.current = document.hidden;
    };

    document.addEventListener('visibilitychange', settleOnVisibilityChange);

    return () =>
      document.removeEventListener('visibilitychange', settleOnVisibilityChange);
  }, [accrueActiveSection]);

  // Outer wrappers skip a native event the innermost section already handled.
  const focusSection = useCallback(
    (section: FormSection | null, event: FocusEvent) => {
      if (lastHandledFocusRef.current === event.nativeEvent) return;
      lastHandledFocusRef.current = event.nativeEvent;

      accrueActiveSection();
      activeSectionRef.current = section ? { section, startedAt: Date.now() } : null;

      const isUntrackedOrAlreadyFocused =
        !section || focusedSectionsRef.current.has(section);
      if (isUntrackedOrAlreadyFocused) return;
      focusedSectionsRef.current.add(section);
      AnalyticsController.track('form_section_focused', {
        form,
        is_editing: isEditing,
        section,
      });
    },
    [accrueActiveSection, form, isEditing],
  );

  const getSectionMs = useCallback(() => {
    accrueActiveSection();

    return { ...sectionMsRef.current };
  }, [accrueActiveSection]);

  const pauseSectionTiming = useCallback(() => {
    accrueActiveSection();
    activeSectionRef.current = null;
  }, [accrueActiveSection]);

  const pauseWhenFocusLeavesForm = useCallback(
    (event: FocusEvent) => {
      const isFocusStayingInForm = event.currentTarget.contains(event.relatedTarget);
      if (!isFocusStayingInForm) {
        pauseSectionTiming();
      }
    },
    [pauseSectionTiming],
  );

  const resetSectionTiming = useCallback(() => {
    sectionMsRef.current = {};
    focusedSectionsRef.current = new Set();
    activeSectionRef.current = null;
  }, []);

  return {
    focusSection,
    getSectionMs,
    pauseSectionTiming,
    pauseWhenFocusLeavesForm,
    resetSectionTiming,
  };
}
